import { useEffect, useRef, useState } from 'react';
import { SelectionInfo } from 'dragonmouse-shared/devigner';
import { Frame } from 'dragonmouse-shared/interfaces';
import NumberInput from 'components/Widget/Input/NumberInput';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { applyStyleProperties } from 'visualEdits';
import { trackVisualEditAction } from 'analytics';
import Field from '../Field';
import * as S from './styles';

interface PaddingEditControlProps {
	frame: Frame;
	selectionInfo: SelectionInfo;
}

const parsePx = (raw: string | undefined): number => {
	if (!raw) return 0;
	const parsed = parseFloat(raw);
	return isNaN(parsed) ? 0 : parsed;
};

const PaddingEditControl = ({ frame, selectionInfo }: PaddingEditControlProps) => {
	const elementId = selectionInfo.dataset?.mcElementId ?? selectionInfo.element.id;

	const initialTop = parsePx(selectionInfo.computedStyles?.paddingTop);
	const initialRight = parsePx(selectionInfo.computedStyles?.paddingRight);
	const initialBottom = parsePx(selectionInfo.computedStyles?.paddingBottom);
	const initialLeft = parsePx(selectionInfo.computedStyles?.paddingLeft);
	const initialUniform =
		initialTop === initialRight &&
		initialRight === initialBottom &&
		initialBottom === initialLeft;

	const [top, setTop] = useState(initialTop);
	const [right, setRight] = useState(initialRight);
	const [bottom, setBottom] = useState(initialBottom);
	const [left, setLeft] = useState(initialLeft);
	// Auto-start in per-side mode when the existing padding isn't uniform —
	// the four inputs themselves communicate the mixed state.
	const [perSide, setPerSide] = useState(!initialUniform);

	const debouncedTop = useDebouncedValue(top);
	const debouncedRight = useDebouncedValue(right);
	const debouncedBottom = useDebouncedValue(bottom);
	const debouncedLeft = useDebouncedValue(left);

	// "Last emitted" snapshot: used to detect which sides changed since the
	// previous emit so we can batch the diff into a single applyStyleProperties
	// call (rather than firing four concurrent writes when single-mode updates
	// all four state values at once).
	const lastEmittedRef = useRef({
		top: initialTop,
		right: initialRight,
		bottom: initialBottom,
		left: initialLeft,
	});

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	useEffect(() => {
		const changes: Record<string, string> = {};
		if (debouncedTop !== lastEmittedRef.current.top) {
			changes['padding-top'] = `${debouncedTop}px`;
		}
		if (debouncedRight !== lastEmittedRef.current.right) {
			changes['padding-right'] = `${debouncedRight}px`;
		}
		if (debouncedBottom !== lastEmittedRef.current.bottom) {
			changes['padding-bottom'] = `${debouncedBottom}px`;
		}
		if (debouncedLeft !== lastEmittedRef.current.left) {
			changes['padding-left'] = `${debouncedLeft}px`;
		}
		if (Object.keys(changes).length === 0) return;

		lastEmittedRef.current = {
			top: debouncedTop,
			right: debouncedRight,
			bottom: debouncedBottom,
			left: debouncedLeft,
		};
		void commit(changes);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedTop, debouncedRight, debouncedBottom, debouncedLeft]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const commit = async (styles: Record<string, string>) => {
		if (!frame.siteId) return;
		trackVisualEditAction('commit_padding_edit');
		await applyStyleProperties(frame.id, frame.siteId, elementId, styles);
	};

	// Single-mode change: drives all four sides at once. The collapsed useEffect
	// above sees all four debouncedValues change in the same tick and emits one
	// batched applyStyleProperties call.
	const onSingleChange = (val: number) => {
		setTop(val);
		setRight(val);
		setBottom(val);
		setLeft(val);
	};

	const onTogglePerSide = () => {
		if (perSide) {
			// Collapsing back to single — equalize all four to the top value.
			setRight(top);
			setBottom(top);
			setLeft(top);
		}
		setPerSide(!perSide);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<Field
			label="Padding"
			accessory={
				<S.Toggle onClick={onTogglePerSide}>{perSide ? 'Equal' : 'Per side'}</S.Toggle>
			}
		>
			{perSide ? (
				<S.SpatialGrid>
					<S.Slot $area="top">
						<NumberInput label="px" value={top} onChange={setTop} min={0} max={500} />
					</S.Slot>
					<S.Slot $area="left">
						<NumberInput label="px" value={left} onChange={setLeft} min={0} max={500} />
					</S.Slot>
					<S.Slot $area="center" />
					<S.Slot $area="right">
						<NumberInput label="px" value={right} onChange={setRight} min={0} max={500} />
					</S.Slot>
					<S.Slot $area="bottom">
						<NumberInput label="px" value={bottom} onChange={setBottom} min={0} max={500} />
					</S.Slot>
				</S.SpatialGrid>
			) : (
				<NumberInput label="px" value={top} onChange={onSingleChange} min={0} max={500} />
			)}
		</Field>
	);
};

export default PaddingEditControl;
